import { MoreVertical, Play, RotateCcw, Square, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../../lib/api";
import { createLogger } from "../../../../lib/logger";
import { getServiceIcon } from "../../../../lib/serviceIcons";
import { Service, ServiceStatus } from "../../../../types";
import { ConfirmDialog } from "../../../common/ConfirmDialog";
import { DeleteServiceDialog } from "../../../common/DeleteServiceDialog";
import { Button } from "../../../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../ui/dropdown-menu";
import { StatusBadge } from "../../../ui/status-badge";
import { TableCell, TableRow } from "../../../ui/table";

const log = createLogger("ServiceTableRow");

interface ServiceTableRowProps {
  service: Service;
  onRefresh: () => void;
  onDeploymentStarted?: () => void;
}

type ConfirmableAction = "stop" | "restart";

export function ServiceTableRow({
  service,
  onRefresh,
  onDeploymentStarted,
}: ServiceTableRowProps) {
  const navigate = useNavigate();
  // Prevents the stray click event that fires on the TableRow after a Radix
  // AlertDialog closes from triggering row navigation.
  // • Delete: set permanently — the row unmounts on success so no reset needed.
  // • Stop/Restart: set then reset after 300 ms, long enough for the stray
  //   click to be absorbed but not so long it blocks intentional row clicks.
  const blockNavRef = useRef(false);
  const blockNavBriefly = () => {
    blockNavRef.current = true;
    setTimeout(() => { blockNavRef.current = false; }, 300);
  };
  const [isStarting, setIsStarting] = useState(false);
  const [serviceStatus, setServiceStatus] = useState(service.status);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmableAction | null>(
    null
  );

  const handleRowClick = () => {
    if (blockNavRef.current) return;
    if (service.id && service.projectId) {
      navigate(`/projects/${service.projectId}/services/${service.id}`);
    }
  };

  // Performs the action and throws on failure so confirmation dialogs can show
  // the error inline. The caller decides how to surface it.
  const runAction = async (action: "start" | "stop" | "restart") => {
    let response;
    switch (action) {
      case "start":
        response = await api.services.start(service.id);
        break;
      case "stop":
        response = await api.services.stop(service.id);
        break;
      case "restart":
        response = await api.services.restart(service.id);
        break;
    }

    setServiceStatus(response.status as ServiceStatus);
    log.debug("Service Action", response.message);
    onRefresh();

    if (action === "start") {
      onDeploymentStarted?.();
    }
  };

  // Start is non-destructive, so it runs immediately (no confirmation).
  const handleStart = async () => {
    setIsStarting(true);
    try {
      await runAction("start");
    } catch (error: any) {
      log.error(
        "Service Action Error",
        error.message || "Failed to start service"
      );
    } finally {
      setIsStarting(false);
    }
  };

  const handleDeleteService = async () => {
    blockNavRef.current = true;
    await api.services.delete(service.id);
    log.debug("Service archived successfully");
    onRefresh();
  };

  const ServiceIcon = getServiceIcon(service.type);

  return (
    <TableRow className="cursor-pointer" onClick={handleRowClick}>
      <TableCell>
        <StatusBadge kind="service" status={serviceStatus} />
      </TableCell>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <ServiceIcon className="h-4 w-4 text-muted-foreground shrink-0" />
          <span>{service.name}</span>
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {service.type || "—"}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {service.repository?.branch || "—"}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {service.framework || "—"}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {service.port ?? "—"}
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {serviceStatus !== "running" && serviceStatus !== "building" && serviceStatus !== "pending" && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleStart();
                }}
                disabled={isStarting}
              >
                <Play className="mr-2 h-4 w-4" />
                Start
              </DropdownMenuItem>
            )}
            {serviceStatus === "running" && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmAction("stop");
                }}
              >
                <Square className="mr-2 h-4 w-4" />
                Stop
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setConfirmAction("restart");
              }}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Restart
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteDialog(true);
              }}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Stop / Restart confirmation */}
        <ConfirmDialog
          open={confirmAction !== null}
          onOpenChange={(open) => {
            if (!open) setConfirmAction(null);
          }}
          title={
            confirmAction === "restart" ? "Restart service" : "Stop service"
          }
          confirmLabel={confirmAction === "restart" ? "Restart" : "Stop"}
          pendingLabel={
            confirmAction === "restart" ? "Restarting…" : "Stopping…"
          }
          onConfirm={() => {
            blockNavBriefly();
            return runAction(confirmAction ?? "stop");
          }}
          description={
            confirmAction === "restart" ? (
              <p>
                Restart{" "}
                <strong className="text-foreground">{service.name}</strong>? It
                will be briefly unavailable while it restarts.
              </p>
            ) : (
              <p>
                Stop <strong className="text-foreground">{service.name}</strong>
                ? It will remain stopped until you start it again.
              </p>
            )
          }
        />

        {/* Delete (archive) confirmation */}
        <DeleteServiceDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          serviceName={service.name}
          onConfirm={handleDeleteService}
        />
      </TableCell>
    </TableRow>
  );
}
